/**
 * Christmas stencils — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const christmasStencilsPack: ToilePack = {
    slug: 'christmas-stencils',
    category: 'season',
    name: 'Christmas stencils',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'snowflake', label: 'Snowflake stencil', subject: 'A snowflake with six symmetrical points'},
        {key: 'star', label: 'Star stencil', subject: 'A five-pointed star in three nested sizes'},
        {key: 'reindeer', label: 'Reindeer stencil', subject: 'A leaping reindeer silhouette with antlers'},
    ],
    styles: [
        {key: 'single', label: 'Single-layer', style: 'a bold single-layer stencil, large solid cut-shapes with wide bridges', palette: 'Solid black on white', framing: 'Easy to cut, centered with a wide white margin'},
        {key: 'detailed', label: 'Detailed', style: 'a multi-detail layered stencil, finer solid cut-shapes with narrow bridges', palette: 'Solid black on white', framing: 'More intricate, centered with a moderate white margin'},
        {key: 'mini', label: 'Mini', style: 'a small mini stencil, compact solid shapes with delicate bridges', palette: 'Solid black on white', framing: 'For cards and nail art, centered with a wide white margin'},
    ],
    directions: ['a whole word instead', 'for a window', 'as a set of three sizes'],
    directionHint: 'try: a whole word instead',
    seo: {
        title: 'Christmas Stencil Printable: Snowflake, Star, Reindeer',
        metaDescription: 'Nine christmas stencils — a snowflake with bridges, a star, a reindeer — bold, closed shapes built to hold together, three styles.',
        h1: 'Christmas stencil printable: snowflake, star, reindeer',
        tagline: 'Nine stencils, three December subjects, three styles each.',
        intro: 'Cut-ready stencil designs for the December project: a snowflake with bridges, a star, a reindeer, each built bold and closed so the cut-out piece holds together. Ask for a tree, a bell, or your own subject, and the same three styles carry it.',
        useCases: [
            'a window or glass painting stencil',
            'a fabric or shirt stencil for spray paint',
            'a cookie or cake decorating stencil',
            'a wall or sign painting project',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine stencil designs — a snowflake with bridges, a star, a reindeer — bold and closed shapes, in three styles.'},
            {q: 'What are the bridges in the design for?', a: 'Bridges are the small connecting strips left between cut-out sections so the stencil holds its shape once the inner pieces are removed.'},
            {q: 'Can I cut this by hand, or do I need a machine?', a: 'The bold, simple shapes work for hand-cutting with a craft knife or for machine cutting — either way, the bridges keep it intact.'},
            {q: 'Can I get a different subject, like a tree or bell?', a: 'Yes — the direction field takes any Christmas subject, including a tree or a bell.'},
            {q: 'Can I sell items made using this stencil?', a: 'Yes — check the license terms on your account for the specifics of commercial use.'},
        ],
    },
}
